import { useEffect, useState } from 'react';
import { useIsAdmin } from '../../hooks/useIsAdmin';
import { useApiKeys, usePricing, useSavePricing, useWorkspaces } from '../../hooks/useClaudePlatform';
import { useSyncStatus, useTriggerSync } from '../../hooks/useSyncRuns';
import { useToast } from '../../components/Toast';
import { ApiError } from '../../lib/api';
import { fmtDateTime, fmtMonth, fmtUsd } from '../../lib/format';
import type { ApiKeyDto, ModelRateDto, WorkspaceDto } from '../../lib/types';

export function ApiKeys() {
  const isAdmin = useIsAdmin();
  if (!isAdmin) {
    return (
      <div className="bg-white rounded-lg shadow border border-slate-200 p-6">
        <h1 className="text-xl font-bold text-atg-navy">Forbidden</h1>
        <p className="text-slate-600 mt-2">You need the <code>Admin</code> role to view this page.</p>
      </div>
    );
  }
  return <ApiKeysInner />;
}

function ApiKeysInner() {
  const toast = useToast();
  const workspaces = useWorkspaces();
  const keys = useApiKeys();
  const status = useSyncStatus();
  const trigger = useTriggerSync();
  const [open, setOpen] = useState<Record<number, boolean>>({});

  const latest = status.data?.sources.find(s => s.source === 'ClaudePlatform');
  const running = status.data?.inFlight.includes('SyncClaudeApi') ?? false;
  const month = workspaces.data?.[0]?.month;

  async function syncNow() {
    try {
      const r = await trigger.mutateAsync('claude-api');
      if (r.accepted.length > 0) toast.success('Claude API sync queued.');
      for (const rej of r.rejected) toast.warning(`${rej.job}: ${rej.reason}`);
    } catch (e) {
      toast.error(((e as ApiError).body as { error?: string } | undefined)?.error ?? 'Could not start the sync.');
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start gap-3">
        <div>
          <h1 className="text-2xl font-bold text-atg-navy">Claude API keys</h1>
          <p className="text-slate-600 mt-1 text-sm">
            Claude Platform spend by workspace is exact (Admin API cost report). Per-key figures are <span className="font-medium">estimates</span>: usage tokens priced with the rate table below and scaled so each workspace's keys add up to its exact daily cost. Keys and workspaces can be added to spend groups.
          </p>
        </div>
        <div className="ml-auto text-right text-xs text-slate-500">
          <div>{latest ? `Last sync ${fmtDateTime(latest.finishedAt)} · ${latest.status}` : 'Never synced'}</div>
          <button onClick={syncNow} disabled={running || trigger.isPending} className="mt-1 rounded-md bg-atg-navy text-white px-3 py-1.5 text-sm font-medium hover:bg-atg-blue disabled:opacity-50">
            {running ? 'Syncing…' : 'Sync now'}
          </button>
        </div>
      </div>

      {(workspaces.isLoading || keys.isLoading) && <div className="text-slate-500">Loading…</div>}
      {(workspaces.error || keys.error) && <div className="text-red-600 text-sm">Failed to load the Claude Platform catalog.</div>}

      {workspaces.data && keys.data && (
        <div className="bg-white rounded-lg shadow border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-left">
              <tr>
                <th className="px-4 py-2 w-8"></th>
                <th className="px-4 py-2">Workspace</th>
                <th className="px-4 py-2 text-right">Keys</th>
                <th className="px-4 py-2 text-right">{month ? `${fmtMonth(month)} to date` : 'MTD'} <span className="text-xs font-normal text-slate-500">(exact)</span></th>
              </tr>
            </thead>
            <tbody>
              {workspaces.data.map(w => (
                <WorkspaceRows key={w.id} w={w} keys={keys.data.filter(k => k.workspaceId === w.id)} open={!!open[w.id]} toggle={() => setOpen(o => ({ ...o, [w.id]: !o[w.id] }))} />
              ))}
              {workspaces.data.length === 0 && (
                <tr><td colSpan={4} className="px-4 py-8 text-center text-slate-500">No workspaces yet. Add the Admin API key (Anthropic:AdminApiKey) and run a sync.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <PricingEditor />
    </div>
  );
}

function WorkspaceRows({ w, keys, open, toggle }: { w: WorkspaceDto; keys: ApiKeyDto[]; open: boolean; toggle: () => void }) {
  return (
    <>
      <tr className="border-t border-slate-200 cursor-pointer hover:bg-slate-50" onClick={toggle}>
        <td className="px-4 py-3 text-slate-400">{open ? '▾' : '▸'}</td>
        <td className="px-4 py-3">
          <span className="font-medium">{w.name}</span>
          {w.isDefault && <span className="ml-2 text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-600">default</span>}
          {w.isArchived && <span className="ml-2 text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-800">archived</span>}
          <div className="text-xs text-slate-500">{w.anthropicId}</div>
        </td>
        <td className="px-4 py-3 text-right tabular-nums">{w.keyCount}</td>
        <td className="px-4 py-3 text-right tabular-nums font-medium">{fmtUsd(w.mtdUsd)}</td>
      </tr>
      {open && keys.map(k => (
        <tr key={k.id} className="border-t border-slate-100 bg-slate-50/60">
          <td></td>
          <td className="px-4 py-2 pl-10">
            <span className={k.isPseudo ? 'italic text-slate-600' : ''}>{k.name}</span>
            <span className="ml-2 text-xs text-slate-500">{k.isPseudo ? 'cost the usage report could not attribute to a key' : `${k.partialKeyHint ?? k.anthropicId} · ${k.status}`}</span>
          </td>
          <td></td>
          <td className="px-4 py-2 text-right tabular-nums">
            {fmtUsd(k.mtdUsd)} <span className="text-xs px-1.5 py-0.5 rounded bg-orange-100 text-orange-800">est.</span>
          </td>
        </tr>
      ))}
      {open && keys.length === 0 && (
        <tr className="border-t border-slate-100 bg-slate-50/60"><td></td><td colSpan={3} className="px-4 py-2 pl-10 text-xs text-slate-500">No keys seen in this workspace.</td></tr>
      )}
    </>
  );
}

const EMPTY_RATE: ModelRateDto = { id: null, modelPattern: '', inputPerMTok: 0, outputPerMTok: 0, cacheReadPerMTok: 0, cacheWrite5mPerMTok: 0, cacheWrite1hPerMTok: 0, notes: '' };

/** Editable rate table. Prefix match, longest wins; saved as a whole. */
function PricingEditor() {
  const toast = useToast();
  const pricing = usePricing();
  const save = useSavePricing();
  const [rows, setRows] = useState<ModelRateDto[] | null>(null);

  useEffect(() => { if (pricing.data && rows === null) setRows(pricing.data); }, [pricing.data, rows]);

  const edit = (i: number, patch: Partial<ModelRateDto>) => setRows(r => r!.map((row, idx) => idx === i ? { ...row, ...patch } : row));
  const num = (v: string) => (v === '' ? 0 : Number(v));

  async function onSave() {
    if (!rows) return;
    try {
      const saved = await save.mutateAsync(rows.filter(r => r.modelPattern.trim() !== ''));
      setRows(saved);
      toast.success('Rate table saved. The next Claude API sync re-prices per-key estimates.');
    } catch (e) {
      const body = (e as ApiError).body as { errors?: Record<string, string[]>; error?: string } | undefined;
      toast.error(body?.error ?? (body?.errors ? Object.values(body.errors).flat()[0] : 'Could not save rates.'));
    }
  }

  return (
    <section className="bg-white rounded-lg shadow border border-slate-200 p-4 space-y-3">
      <div className="flex items-center gap-3">
        <div>
          <h2 className="text-sm font-semibold text-slate-800">Model rates for per-key estimates</h2>
          <p className="text-xs text-slate-500">USD per million tokens. A model id matches the longest pattern it starts with (e.g. <code>claude-opus-5-5</code> beats <code>claude-opus-5</code>). Built-in defaults seed the table on first sync.</p>
        </div>
        <div className="ml-auto flex gap-2">
          <button onClick={() => setRows(r => [...(r ?? []), { ...EMPTY_RATE }])} className="rounded-md border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50">Add row</button>
          <button onClick={onSave} disabled={save.isPending || rows === null} className="rounded-md bg-atg-navy text-white px-3 py-1.5 text-sm font-medium hover:bg-atg-blue disabled:opacity-50">{save.isPending ? 'Saving…' : 'Save rates'}</button>
        </div>
      </div>
      {pricing.isLoading && <div className="text-slate-500 text-sm">Loading rates…</div>}
      {rows && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-100 text-left">
              <tr>
                <th className="px-2 py-2">Model prefix</th>
                <th className="px-2 py-2 text-right">Input</th>
                <th className="px-2 py-2 text-right">Output</th>
                <th className="px-2 py-2 text-right">Cache read</th>
                <th className="px-2 py-2 text-right">Cache write 5m</th>
                <th className="px-2 py-2 text-right">Cache write 1h</th>
                <th className="px-2 py-2">Notes</th>
                <th className="px-2 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-t border-slate-200">
                  <td className="px-2 py-1"><input value={r.modelPattern} onChange={e => edit(i, { modelPattern: e.target.value })} className="w-44 rounded border border-slate-300 px-2 py-1 font-mono" placeholder="claude-sonnet-4" /></td>
                  {(['inputPerMTok', 'outputPerMTok', 'cacheReadPerMTok', 'cacheWrite5mPerMTok', 'cacheWrite1hPerMTok'] as const).map(f => (
                    <td key={f} className="px-2 py-1 text-right">
                      <input type="number" step="0.01" min={0} value={r[f]} onChange={e => edit(i, { [f]: num(e.target.value) })} className="w-20 rounded border border-slate-300 px-2 py-1 text-right tabular-nums" />
                    </td>
                  ))}
                  <td className="px-2 py-1"><input value={r.notes ?? ''} onChange={e => edit(i, { notes: e.target.value })} className="w-40 rounded border border-slate-300 px-2 py-1" /></td>
                  <td className="px-2 py-1 text-right"><button onClick={() => setRows(rs => rs!.filter((_, idx) => idx !== i))} className="text-red-600 hover:underline">Remove</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
